/**
 * React authentication context for the Word add-in.
 *
 * Provides auth state (session, user, connection status) and actions
 * (login, logout) to all child components. Persists the instance URL
 * in localStorage for convenience.
 *
 * @module auth/auth-context
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { AuthSession, AuthUser } from '@alterspective/shared';
import {
  getAuthorizeUrl,
  openLoginDialog,
  devLogin,
  logout as authLogout,
  getGatewayUrl,
  canUseDevLogin,
} from './auth-service';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface AuthState {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;

  /** Whether authentication is in progress */
  isAuthenticating: boolean;

  /** Current session (null if not authenticated) */
  session: AuthSession | null;

  /** Authenticated user info */
  user: AuthUser | null;

  /** Session token for API calls */
  sessionToken: string | null;

  /** Connected PMS instance URL */
  instanceUrl: string;

  /** Whether the session is in dev mode */
  isDevMode: boolean;

  /** Gateway URL */
  gatewayUrl: string;

  /** Last auth error */
  error: string | null;

  /** Sign in to a PMS instance. Set useDevMode=true for client_credentials bypass. */
  login: (instanceUrl: string, useDevMode?: boolean) => Promise<void>;

  /** Sign out */
  logout: () => Promise<void>;

  /** Set the instance URL (before login) */
  setInstanceUrl: (url: string) => void;

  /** Clear the error */
  clearError: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthState | null>(null);

// ---------------------------------------------------------------------------
// Local storage key for instance URL
// ---------------------------------------------------------------------------

const INSTANCE_URL_KEY = 'alterspective-instance-url';

function loadInstanceUrl(): string {
  try {
    return localStorage.getItem(INSTANCE_URL_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveInstanceUrl(url: string): void {
  try {
    localStorage.setItem(INSTANCE_URL_KEY, url);
  } catch {
    // localStorage may not be available in all Office.js environments
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

function loadTestSession(): AuthSession | null {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mockOffice') !== '1' || !canUseDevLogin(getGatewayUrl())) {
      return null;
    }
    const token = params.get('testToken');
    const instanceUrl = params.get('testInstance');
    if (token && instanceUrl) {
      return { sessionToken: token, user: { userId: 'test', displayName: 'Test User' }, instanceUrl, isDevMode: false };
    }
  } catch { /* ignore */ }
  return null;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<AuthSession | null>(loadTestSession);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [instanceUrl, setInstanceUrlState] = useState(loadInstanceUrl);
  const [error, setError] = useState<string | null>(null);

  const gatewayUrl = useMemo(() => getGatewayUrl(), []);

  const setInstanceUrl = useCallback((url: string) => {
    setInstanceUrlState(url);
    saveInstanceUrl(url);
  }, []);

  const login = useCallback(async (url: string, useDevMode = false) => {
    setError(null);
    setIsAuthenticating(true);
    setInstanceUrl(url);

    try {
      let result: AuthSession;

      if (useDevMode) {
        // Explicit dev-mode login — uses client_credentials via gateway
        result = await devLogin(gatewayUrl, url);
      } else {
        // Production path — OAuth via Dialog API
        const { authorizeUrl, state } = await getAuthorizeUrl(gatewayUrl, url);
        result = await openLoginDialog(gatewayUrl, authorizeUrl, state);
      }

      setSession(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Authentication failed';
      setError(message);
      console.error('[Auth] Login failed:', message);
    } finally {
      setIsAuthenticating(false);
    }
  }, [gatewayUrl, setInstanceUrl]);

  const logout = useCallback(async () => {
    if (session?.sessionToken) {
      try {
        await authLogout(gatewayUrl, session.sessionToken);
      } catch {
        // Logout failure is not critical — clear local state anyway
      }
    }
    setSession(null);
    setError(null);
  }, [session, gatewayUrl]);

  const clearError = useCallback(() => setError(null), []);

  const value: AuthState = useMemo(
    () => ({
      isAuthenticated: session !== null,
      isAuthenticating,
      session,
      user: session?.user ?? null,
      sessionToken: session?.sessionToken ?? null,
      instanceUrl,
      isDevMode: session?.isDevMode ?? false,
      gatewayUrl,
      error,
      login,
      logout,
      setInstanceUrl,
      clearError,
    }),
    [session, isAuthenticating, instanceUrl, gatewayUrl, error, login, logout, setInstanceUrl, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Use the auth context. Must be called within an AuthProvider. */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
